import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Artifact } from '../../../types/artifact';
import { ArtifactRelationship } from '../../../types/artifactRelationship';
import './NetworkView.css';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  type: string;
  isSelected: boolean;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link extends d3.SimulationLinkDatum<Node> {
  source: Node;
  target: Node;
  type: string;
}

interface NetworkViewProps {
  artifacts: Artifact[];
  relationships: ArtifactRelationship[];
  selectedArtifactId: string | null;
  onArtifactSelect: (artifactId: string) => void;
}

export const NetworkView: React.FC<NetworkViewProps> = ({
  artifacts,
  relationships,
  selectedArtifactId,
  onArtifactSelect,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !artifacts.length) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Create the SVG container
    const svg = d3.select(svgRef.current);
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Create the nodes data structure
    const nodes: Node[] = artifacts.map((artifact) => ({
      id: artifact.id,
      name: artifact.name,
      type: artifact.type,
      isSelected: artifact.id === selectedArtifactId,
    }));

    // Create the links data structure
    const links: Link[] = relationships
      .filter((rel) => {
        const sourceNode = nodes.find((n) => n.id === rel.source_artifact_id);
        const targetNode = nodes.find((n) => n.id === rel.target_artifact_id);
        return sourceNode && targetNode;
      })
      .map((rel) => ({
        source: nodes.find((n) => n.id === rel.source_artifact_id)!,
        target: nodes.find((n) => n.id === rel.target_artifact_id)!,
        type: rel.relationship_type,
      }));

    // Create the force simulation
    const simulation = d3
      .forceSimulation<Node>(nodes)
      .force(
        'link',
        d3.forceLink<Node, Link>(links).id((d) => d.id)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    // Create the links
    const link = svg
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('class', 'link')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1);

    // Create the nodes
    const node = svg.append('g').selectAll('g').data(nodes).join('g').attr('class', 'node');

    // Add drag behavior
    const drag = d3
      .drag<SVGGElement, Node>()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);

    // Apply drag behavior to nodes
    (node as d3.Selection<SVGGElement, Node, SVGGElement, unknown>).call(drag);

    // Add circles to nodes
    node
      .append('circle')
      .attr('r', 20)
      .attr('fill', (d) => (d.isSelected ? '#ff4444' : '#69b3a2'))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    // Add labels to nodes
    node
      .append('text')
      .text((d) => d.name)
      .attr('x', 25)
      .attr('y', 5)
      .attr('class', 'node-label');

    // Add click handler
    node.on('click', (event, d) => {
      onArtifactSelect(d.id);
    });

    // Update positions on each tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => d.source.x!)
        .attr('y1', (d) => d.source.y!)
        .attr('x2', (d) => d.target.x!)
        .attr('y2', (d) => d.target.y!);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [artifacts, relationships, selectedArtifactId, onArtifactSelect]);

  return <svg ref={svgRef} className="network-view" width="100%" height="100%" />;
};
